/**
 * Guards against raw Plate (Slate) editor JSON leaking into a sent message body
 * or a rendered preview/summary.
 *
 * The merchant composer autosaves drafts with their *editor-serialized* value —
 * a top-level JSON array of Plate nodes, e.g.
 * `[{"type":"p","children":[{"text":"hi"}],"id":"abc"}]`. The send pipeline,
 * by contrast, treats a draft's `content` as the renderable message body (HTML
 * or plain text) and dispatches it verbatim to the channel. If a draft is sent
 * while its `content` still holds the autosaved Plate JSON (e.g. a debounced
 * autosave races ahead of the send-time HTML save), that raw JSON is delivered
 * as the message — customers and agents then see `[{"type":"p",...}]`. The same
 * raw value also surfaces in support-ticket list summaries when a ticket has no
 * AI insights summary and falls back to the latest message's stored content.
 *
 * These helpers make that impossible: any content that is detectably serialized
 * Plate JSON is collapsed to its plain text. Content that is already HTML or
 * plain text is returned untouched, so the normal path is a no-op.
 */

interface PlateNode {
  readonly text?: unknown;
  readonly children?: readonly unknown[];
}

const isPlateNode = (value: unknown): value is PlateNode =>
  typeof value === "object" &&
  value !== null &&
  (Array.isArray((value as PlateNode).children) ||
    typeof (value as PlateNode).text === "string");

/**
 * True when `content` is a serialized Plate value: a non-empty JSON array whose
 * every element is a Plate node (an element with `children`, or a text leaf).
 * Plain text and HTML never satisfy this — HTML does not start with `[`, and a
 * stray `[...]` that isn't node-shaped fails the per-element check.
 */
export function isSerializedPlateValue(content: string): boolean {
  const trimmed = content.trimStart();
  if (!trimmed.startsWith("[")) {
    return false;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return false;
  }
  return (
    Array.isArray(parsed) && parsed.length > 0 && parsed.every(isPlateNode)
  );
}

const nodeToText = (node: unknown): string => {
  if (!isPlateNode(node)) {
    return "";
  }
  if (typeof node.text === "string") {
    return node.text;
  }
  if (Array.isArray(node.children)) {
    return node.children.map(nodeToText).join("");
  }
  return "";
};

/**
 * Convert a parsed Plate value (e.g. live `editor.children`) to plain text, one
 * top-level block per line so paragraph breaks survive as `\n`.
 */
export function plateNodesToPlainText(nodes: readonly unknown[]): string {
  return nodes.map(nodeToText).join("\n");
}

/**
 * Extract the plain text from a serialized Plate value, one block per line.
 * Assumes `content` is serialized Plate JSON (guard with
 * {@link isSerializedPlateValue} first); returns `content` unchanged if it
 * cannot be parsed.
 */
export function plateValueToPlainText(content: string): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content.trimStart());
  } catch {
    return content;
  }
  if (!Array.isArray(parsed)) {
    return content;
  }
  return plateNodesToPlainText(parsed);
}

/**
 * Send-boundary / display guard: if `content` is serialized Plate JSON, collapse
 * it to plain text; otherwise return it unchanged. Use this anywhere a draft's
 * `content` (or an inline channel payload's `content`) becomes the dispatched
 * message body, or where stored message content is surfaced as a summary/preview,
 * so a message can never go out — or be shown — as raw editor JSON.
 */
export function ensureNotSerializedPlate(content: string): string {
  return isSerializedPlateValue(content)
    ? plateValueToPlainText(content)
    : content;
}
